/**
 * @fileoverview Organisation Settings Page
 *
 * Purpose: Displays all organisations the user belongs to, with org details,
 *          a members table, and pending invitations. Owners can edit the org
 *          name, change member roles, edit member names, invite new members,
 *          revoke invites, and remove members. Includes a roles explainer card.
 * URL: /dashboard/settings/organization
 * Protection: Requires authentication
 *
 * Dependencies:
 * - Convex: organizations.queries.getUserOrganizations,
 *           organizations.queries.getOrganizationMembers,
 *           organizations.mutations.updateOrganizationName,
 *           organizations.mutations.updateMemberRole,
 *           organizations.mutations.updateMemberName,
 *           organizations.mutations.inviteMember,
 *           organizations.mutations.getOrganizationInvitations,
 *           organizations.mutations.revokeInvitation,
 *           organizations.mutations.removeMember
 * - Components: Table, Badge, Button, Input, Select, Dialog, AlertDialog
 */

import { createFileRoute } from "@tanstack/react-router";
import {
  convexQuery,
  useConvexMutation,
  useConvexAction,
} from "@convex-dev/react-query";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAction } from "convex/react";
import { api } from "~/convex/_generated/api";
import type { Id } from "~/convex/_generated/dataModel";
import type { JobRole } from "@cvx/jobs/schema";
import type { OrgRole } from "@cvx/organizations/orgMemberships/schema";
import { AU_JURISDICTIONS } from "@cvx/lib/businessDays";
import { Alert, AlertDescription, Badge, Button, Input } from "@/ui/primitives";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/ui/primitives";
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from "@/ui/composed/data-display";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from "@/ui/composed/overlays";
import {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/ui/composed/overlays";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/ui/composed/overlays";
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "@/ui/composed/menus";
import {
  AlertCircle,
  Building2,
  Crown,
  Info,
  LogOut,
  Mail,
  Pencil,
  Shield,
  User,
  UserMinus,
  UserPlus,
  Users,
  X,
} from "lucide-react";
import { UserAvatar, getDisplayName } from "@/ui/composed";
import { JobRoleSelect } from "@/components/jobs/JobRoleSelect";
import { WorkflowRoleSelect } from "@/components/settings/tasks/WorkflowRoleSelect";
import { useForm } from "@tanstack/react-form";
import { z } from "zod";
import { useState, type ReactNode } from "react";
import { toast } from "sonner";

const GROUP_CARD_CLASS =
  "rounded-xl bg-background shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.06)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.1),0_2px_4px_rgba(0,0,0,0.06)] transition-shadow duration-200 overflow-hidden";

export const Route = createFileRoute(
  "/_app/_auth/dashboard/_layout/settings/organization",
)({
  component: OrganizationSettings,
  beforeLoad: () => ({
    title: "Organisation",
    headerTitle: "Organisation",
    headerDescription: "Manage your organisations and members.",
  }),
});

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface OrgWithRole {
  _id: Id<"organizations">;
  _creationTime: number;
  workOsOrgId: string;
  name: string;
  slug?: string;
  role: OrgRole;
}

interface OrgMember {
  _id: Id<"orgMemberships">;
  role: OrgRole;
  user: {
    _id: Id<"users">;
    email: string;
    firstName?: string;
    lastName?: string;
    avatarUrl?: string;
  };
}

interface PendingInvitation {
  id: string;
  email: string;
  state: string;
  createdAt: string;
  expiresAt: string;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** React Query key factory for org invitations */
const invitationsQueryKey = (orgId: Id<"organizations">) => [
  "orgInvitations",
  orgId,
];

// ---------------------------------------------------------------------------
// Validators
// ---------------------------------------------------------------------------

const orgNameSchema = z
  .string()
  .min(2, "Organisation name must be at least 2 characters")
  .max(100, "Organisation name must be at most 100 characters")
  .trim();

const inviteEmailSchema = z
  .string()
  .email("Please enter a valid email address")
  .trim();

/**
 * Extracts a user-friendly invite error message from Convex action failures.
 */
function getInviteErrorMessage(error: unknown): string {
  const fallback = "We couldn't send the invitation. Please try again.";

  if (typeof error === "object" && error !== null && "data" in error) {
    const data = (error as { data?: unknown }).data;
    if (typeof data === "string" && data.trim()) {
      return data.trim();
    }
    if (
      typeof data === "object" &&
      data !== null &&
      "message" in data &&
      typeof (data as { message?: unknown }).message === "string"
    ) {
      return ((data as { message: string }).message ?? "").trim() || fallback;
    }
  }

  const rawMessage =
    error instanceof Error
      ? error.message
      : typeof error === "string"
        ? error
        : fallback;

  const uncaughtErrorMatch = rawMessage.match(
    /Uncaught Error:\s*(.*?)(?:\s+at handler|$)/i,
  );
  if (uncaughtErrorMatch?.[1]) {
    return uncaughtErrorMatch[1].trim();
  }

  return rawMessage.trim() || fallback;
}

// ---------------------------------------------------------------------------
// Main Page Component
// ---------------------------------------------------------------------------

export default function OrganizationSettings() {
  const { data: userOrgs } = useQuery(
    convexQuery(api.organizations.queries.getUserOrganizations, {}),
  );

  if (userOrgs === undefined) {
    return <OrganizationSettingsSkeleton />;
  }

  if (userOrgs.length === 0) {
    return (
      <div className="flex h-full w-full flex-col items-center justify-center gap-4 py-16">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
          <Building2 className="h-8 w-8 text-primary/60" />
        </span>
        <div className="flex flex-col items-center gap-1">
          <h3 className="text-lg font-medium text-primary">No Organisations</h3>
          <p className="text-sm text-primary/60">
            You are not a member of any organisation yet.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full w-full flex-col gap-8">
      {userOrgs.map((org: OrgWithRole) => (
        <OrganizationSection key={org._id} org={org} />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Per-Organisation Section
// ---------------------------------------------------------------------------

function OrganizationSection({ org }: { org: OrgWithRole }) {
  const { data: members } = useQuery(
    convexQuery(api.organizations.queries.getOrganizationMembers, {
      orgId: org._id,
    }),
  );

  const { data: currentUser } = useQuery(
    convexQuery(api.users.queries.getCurrentUser, {}),
  );

  const isOwner = org.role === "owner";
  const isOwnerOrAdmin = org.role === "owner" || org.role === "admin";

  // Fetch pending invitations from WorkOS (only for owners/admins)
  const getInvitations = useAction(
    api.organizations.mutations.getOrganizationInvitations,
  );
  const { data: invitations } = useQuery({
    queryKey: invitationsQueryKey(org._id),
    queryFn: () => getInvitations({ orgId: org._id }),
    enabled: isOwnerOrAdmin,
  });

  return (
    <div className="flex flex-col gap-6">
      {/* Organisation Info Card (editable name for owners) */}
      {isOwner ? (
        <EditableOrgNameCard org={org} memberCount={members?.length} />
      ) : (
        <ReadOnlyOrgCard org={org} memberCount={members?.length} />
      )}

      {/* Roles Explainer */}
      <RolesExplainer />

      {/* Members & Invitations Table */}
      <div className={`flex w-full flex-col ${GROUP_CARD_CLASS}`}>
        <div className="flex items-center justify-between p-6 pb-0">
          <div className="flex flex-col gap-2">
            <h3 className="text-lg font-medium text-primary">Members</h3>
            <p className="text-sm text-primary/60">
              People who are part of this organisation.
            </p>
          </div>
          {isOwner && <InviteMemberDialog orgId={org._id} />}
        </div>

        {members === undefined ? (
          <div className="p-6">
            <MembersTableSkeleton />
          </div>
        ) : members.length === 0 &&
          (!invitations || invitations.length === 0) ? (
          <div className="flex items-center justify-center p-6">
            <p className="text-sm text-primary/60">No members found.</p>
          </div>
        ) : (
          <div className="p-6 pt-4">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">Avatar</TableHead>
                  <TableHead>First Name</TableHead>
                  <TableHead>Last Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  {isOwnerOrAdmin && (
                    <TableHead className="w-20">Actions</TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {/* Active members */}
                {members.map((member: OrgMember) => {
                  const isSelf = currentUser?._id === member.user._id;
                  // Owner/admin can remove others (admin cannot remove owners)
                  const canRemove =
                    isOwnerOrAdmin &&
                    !isSelf &&
                    !(org.role === "admin" && member.role === "owner");

                  return (
                    <TableRow key={member._id}>
                      <TableCell>
                        <UserAvatar
                          avatarUrl={member.user.avatarUrl}
                          firstName={member.user.firstName}
                          lastName={member.user.lastName}
                          email={member.user.email}
                          className="h-8 w-8"
                        />
                      </TableCell>
                      <TableCell>
                        {member.user.firstName || (
                          <span className="text-primary/40">&mdash;</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {member.user.lastName || (
                          <span className="text-primary/40">&mdash;</span>
                        )}
                      </TableCell>
                      <TableCell className="text-primary/80">
                        {member.user.email}
                      </TableCell>
                      <TableCell>
                        {isOwner ? (
                          <RoleSelect member={member} isCurrentUser={isSelf} />
                        ) : (
                          <RoleBadge role={member.role} />
                        )}
                      </TableCell>
                      {isOwnerOrAdmin && (
                        <TableCell>
                          <div className="flex items-center gap-1">
                            {isOwner && (
                              <EditMemberNameDialog member={member} />
                            )}
                            {canRemove && (
                              <RemoveMemberButton member={member} />
                            )}
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}

                {/* Pending invitations (owner/admin) */}
                {isOwnerOrAdmin &&
                  invitations?.map((invite: PendingInvitation) => (
                    <PendingInviteRow
                      key={invite.id}
                      invite={invite}
                      orgId={org._id}
                    />
                  ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Teams (MOR-722) */}
      <TeamsCard
        orgId={org._id}
        canManage={isOwnerOrAdmin}
        orgMembers={members ?? []}
      />

      {/* Default job access (MOR-722) */}
      <DefaultJobAccessCard orgId={org._id} canManage={isOwnerOrAdmin} />

      {/* Calendar & workflow task defaults (MOR-836 / MOR-839) */}
      <CalendarTasksCard orgId={org._id} canManage={isOwnerOrAdmin} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Roles Explainer
// ---------------------------------------------------------------------------

/**
 * Card that explains the three organisation roles and their permissions.
 */
function RolesExplainer() {
  return (
    <div className={`flex w-full flex-col ${GROUP_CARD_CLASS}`}>
      <div className="flex items-center gap-2 p-6 pb-3">
        <Info className="h-4 w-4 text-primary/60" />
        <h3 className="text-sm font-medium text-primary">Organisation Roles</h3>
      </div>
      <div className="grid grid-cols-1 gap-4 px-6 pb-6 md:grid-cols-3">
        <div className="flex flex-col gap-2 rounded-md border border-border/50 bg-secondary/50 p-4 dark:bg-card">
          <div className="flex items-center gap-2">
            <Crown className="h-4 w-4 text-primary" />
            <span className="text-sm font-medium text-primary">Owner</span>
          </div>
          <p className="text-xs leading-relaxed text-primary/60">
            Full control over the organisation. Can rename the org, invite and
            remove members, and change anyone's role.
          </p>
        </div>
        <div className="flex flex-col gap-2 rounded-md border border-border/50 bg-secondary/50 p-4 dark:bg-card">
          <div className="flex items-center gap-2">
            <Shield className="h-4 w-4 text-primary/80" />
            <span className="text-sm font-medium text-primary">Admin</span>
          </div>
          <p className="text-xs leading-relaxed text-primary/60">
            Can manage members and change roles (except owners). Cannot rename
            the organisation or remove owners.
          </p>
        </div>
        <div className="flex flex-col gap-2 rounded-md border border-border/50 bg-secondary/50 p-4 dark:bg-card">
          <div className="flex items-center gap-2">
            <User className="h-4 w-4 text-primary/60" />
            <span className="text-sm font-medium text-primary">Member</span>
          </div>
          <p className="text-xs leading-relaxed text-primary/60">
            Standard access to the organisation's resources. Cannot manage
            members or change settings.
          </p>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pending Invitation Row (with confirmation dialog)
// ---------------------------------------------------------------------------

/**
 * Renders a table row for a pending WorkOS invitation with a revoke button
 * that requires confirmation via an AlertDialog.
 */
function PendingInviteRow({
  invite,
  orgId,
}: {
  invite: PendingInvitation;
  orgId: Id<"organizations">;
}) {
  const queryClient = useQueryClient();

  const { mutateAsync: revokeInvite, isPending } = useMutation({
    mutationFn: useConvexAction(api.organizations.mutations.revokeInvitation),
  });

  const handleRevoke = async () => {
    try {
      await revokeInvite({ orgId, invitationId: invite.id });
      toast.success(`Invitation to ${invite.email} revoked`);
      await queryClient.invalidateQueries({
        queryKey: invitationsQueryKey(orgId),
      });
    } catch {
      toast.error("Failed to revoke invitation");
    }
  };

  return (
    <TableRow className="opacity-60">
      <TableCell>
        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-muted">
          <Mail className="h-4 w-4 text-primary/40" />
        </span>
      </TableCell>
      <TableCell>
        <span className="text-primary/40">&mdash;</span>
      </TableCell>
      <TableCell>
        <span className="text-primary/40">&mdash;</span>
      </TableCell>
      <TableCell className="text-primary/80">{invite.email}</TableCell>
      <TableCell>
        <Badge variant="outline" className="border-dashed">
          Pending
        </Badge>
      </TableCell>
      <TableCell>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              size="sm"
              variant="ghost"
              className="h-7 w-7 p-0 text-destructive hover:bg-destructive/10 hover:text-destructive"
              disabled={isPending}
              title="Revoke invitation"
            >
              <X className="h-4 w-4" />
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Revoke invitation?</AlertDialogTitle>
              <AlertDialogDescription>
                This will cancel the pending invitation to{" "}
                <span className="font-medium text-foreground">
                  {invite.email}
                </span>
                . They will no longer be able to join the organisation using
                this invite link.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleRevoke}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {isPending ? "Revoking..." : "Revoke Invitation"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </TableCell>
    </TableRow>
  );
}

// ---------------------------------------------------------------------------
// Remove Member Button (with confirmation dialog)
// ---------------------------------------------------------------------------

/**
 * Button that opens an AlertDialog to confirm removing a member from the org.
 */
function RemoveMemberButton({ member }: { member: OrgMember }) {
  const { mutateAsync: removeMember, isPending } = useMutation({
    mutationFn: useConvexAction(api.organizations.mutations.removeMember),
  });

  const displayName = getDisplayName(
    member.user.firstName,
    member.user.lastName,
    member.user.email,
  );

  const handleRemove = async () => {
    try {
      await removeMember({ membershipId: member._id });
      toast.success(`${displayName} has been removed`);
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : "Failed to remove member";
      toast.error(message);
    }
  };

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button
          size="sm"
          variant="ghost"
          className="h-7 w-7 p-0 text-destructive hover:bg-destructive/10 hover:text-destructive"
          disabled={isPending}
          title="Remove member"
        >
          <UserMinus className="h-4 w-4" />
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Remove member?</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to remove{" "}
            <span className="font-medium text-foreground">{displayName}</span>{" "}
            from this organisation? They will lose access to all organisation
            resources immediately.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleRemove}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {isPending ? "Removing..." : "Remove Member"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// ---------------------------------------------------------------------------
// Edit Member Name Dialog (owner-only, MOR-972)
// ---------------------------------------------------------------------------

/**
 * Pencil button that opens a dialog where the owner can set or correct a
 * member's first/last name. Useful when members never complete their
 * profile, leaving generated documents to fall back to the email address.
 */
function EditMemberNameDialog({ member }: { member: OrgMember }) {
  const [open, setOpen] = useState(false);
  const [firstName, setFirstName] = useState(member.user.firstName ?? "");
  const [lastName, setLastName] = useState(member.user.lastName ?? "");

  const { mutateAsync: updateName, isPending } = useMutation({
    mutationFn: useConvexAction(api.organizations.mutations.updateMemberName),
  });

  const displayName = getDisplayName(
    member.user.firstName,
    member.user.lastName,
    member.user.email,
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!firstName.trim() && !lastName.trim()) {
      toast.error("Enter at least a first or last name");
      return;
    }
    try {
      await updateName({
        membershipId: member._id,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
      });
      toast.success(`Name updated for ${member.user.email}`);
      setOpen(false);
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : "Failed to update name";
      toast.error(message);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (nextOpen) {
          // Re-seed from the latest member data each time the dialog opens
          setFirstName(member.user.firstName ?? "");
          setLastName(member.user.lastName ?? "");
        }
      }}
    >
      <DialogTrigger asChild>
        <Button
          size="sm"
          variant="ghost"
          className="h-7 w-7 p-0 text-primary/60 hover:text-primary"
          title={`Edit name for ${displayName}`}
        >
          <Pencil className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Edit member name</DialogTitle>
            <DialogDescription>
              Set the name shown across the platform and in generated documents
              for{" "}
              <span className="font-medium text-foreground">
                {member.user.email}
              </span>
              .
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 gap-4 py-4 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <span className="text-xs font-medium text-primary/60">
                First name
              </span>
              <Input
                placeholder="First name"
                autoComplete="off"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                maxLength={100}
                autoFocus
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <span className="text-xs font-medium text-primary/60">
                Last name
              </span>
              <Input
                placeholder="Last name"
                autoComplete="off"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                maxLength={100}
              />
            </div>
          </div>

          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline">
                Cancel
              </Button>
            </DialogClose>
            <Button
              type="submit"
              disabled={isPending || (!firstName.trim() && !lastName.trim())}
            >
              {isPending ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Leave Organisation Button (non-owners only)
// ---------------------------------------------------------------------------

/**
 * Button that opens an AlertDialog to confirm leaving an organisation.
 * Only shown for admin and member roles (owners cannot leave).
 */
function LeaveOrganizationButton({ org }: { org: OrgWithRole }) {
  const { mutateAsync: leaveOrg, isPending } = useMutation({
    mutationFn: useConvexAction(api.organizations.mutations.leaveOrganization),
  });

  const handleLeave = async () => {
    try {
      await leaveOrg({ orgId: org._id });
      toast.success(`You have left ${org.name}`);
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : "Failed to leave organisation";
      toast.error(message);
    }
  };

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button
          size="sm"
          variant="outline"
          className="gap-2 border-destructive/30 text-destructive hover:bg-destructive/10 hover:text-destructive"
          disabled={isPending}
        >
          <LogOut className="h-4 w-4" />
          Leave Organisation
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Leave organisation?</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to leave{" "}
            <span className="font-medium text-foreground">{org.name}</span>? You
            will lose access to all organisation resources immediately. You will
            need a new invitation to rejoin.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleLeave}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {isPending ? "Leaving..." : "Leave Organisation"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// ---------------------------------------------------------------------------
// Editable Organisation Name Card (owner-only)
// ---------------------------------------------------------------------------

/**
 * Card with an inline form to edit the organisation name.
 * Only rendered when the current user is the org owner.
 */
function EditableOrgNameCard({
  org,
  memberCount,
}: {
  org: OrgWithRole;
  memberCount?: number;
}) {
  const { mutateAsync: updateName, isPending } = useMutation({
    mutationFn: useConvexAction(
      api.organizations.mutations.updateOrganizationName,
    ),
  });

  const form = useForm({
    defaultValues: {
      name: org.name,
    },
    onSubmit: async ({ value }) => {
      try {
        await updateName({ orgId: org._id, name: value.name });
        toast.success("Organisation name updated");
      } catch {
        toast.error("Failed to update organisation name");
      }
    },
  });

  return (
    <form
      className={`flex w-full flex-col ${GROUP_CARD_CLASS}`}
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        form.handleSubmit();
      }}
    >
      <div className="flex flex-col gap-4 p-6">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
            <Building2 className="h-5 w-5 text-primary" />
          </span>
          <div className="flex flex-col gap-1">
            <h2 className="text-xl font-medium text-primary">
              Organisation Name
            </h2>
            <p className="text-sm text-primary/60">
              Update your organisation's display name.
            </p>
          </div>
        </div>
        <form.Field
          name="name"
          validators={{
            onSubmit: orgNameSchema,
          }}
          children={(field) => (
            <div className="flex flex-col gap-1">
              <Input
                placeholder="Organisation name"
                autoComplete="off"
                required
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(e.target.value)}
                className={`w-80 bg-transparent ${
                  field.state.meta?.errors.length > 0
                    ? "border-destructive focus-visible:ring-destructive"
                    : ""
                }`}
              />
              {field.state.meta?.errors.length > 0 && (
                <p className="text-sm text-destructive dark:text-destructive-foreground">
                  {field.state.meta.errors
                    .map((e) => (typeof e === "string" ? e : e?.message))
                    .filter(Boolean)
                    .join(" ")}
                </p>
              )}
            </div>
          )}
        />
      </div>
      <div className="flex min-h-14 w-full items-center justify-between border-t border-border/50 bg-secondary/50 px-6 dark:bg-card">
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-2">
            <span className="text-sm text-primary/60">Your role:</span>
            <RoleBadge role={org.role} />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-primary/60">Members:</span>
            <span className="text-sm font-medium text-primary">
              {memberCount ?? "..."}
            </span>
          </div>
        </div>
        <Button type="submit" size="sm" disabled={isPending}>
          {isPending ? "Saving..." : "Save"}
        </Button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Read-Only Organisation Card (non-owners)
// ---------------------------------------------------------------------------

/**
 * Static card that displays organisation info without edit controls.
 * Includes a "Leave Organisation" button for admins and members (not owners).
 */
function ReadOnlyOrgCard({
  org,
  memberCount,
}: {
  org: OrgWithRole;
  memberCount?: number;
}) {
  return (
    <div className={`flex w-full flex-col ${GROUP_CARD_CLASS}`}>
      <div className="flex flex-col gap-4 p-6">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
            <Building2 className="h-5 w-5 text-primary" />
          </span>
          <h2 className="text-xl font-medium text-primary">{org.name}</h2>
        </div>
      </div>
      <div className="flex min-h-14 w-full items-center justify-between border-t border-border/50 bg-secondary/50 px-6 dark:bg-card">
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-2">
            <span className="text-sm text-primary/60">Your role:</span>
            <RoleBadge role={org.role} />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-primary/60">Members:</span>
            <span className="text-sm font-medium text-primary">
              {memberCount ?? "..."}
            </span>
          </div>
        </div>
        <LeaveOrganizationButton org={org} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Role Select Dropdown (owner-only, per-member)
// ---------------------------------------------------------------------------

/**
 * Dropdown to change a member's role. Disabled for the current user's own row
 * to prevent accidental self-demotion.
 */
function RoleSelect({
  member,
  isCurrentUser,
}: {
  member: OrgMember;
  isCurrentUser: boolean;
}) {
  const { mutateAsync: updateRole } = useMutation({
    mutationFn: useConvexMutation(api.organizations.mutations.updateMemberRole),
  });

  const handleRoleChange = async (newRole: string) => {
    try {
      await updateRole({
        membershipId: member._id,
        role: newRole as OrgRole,
      });
      toast.success(`Role updated to ${newRole}`);
    } catch {
      toast.error("Failed to update role");
    }
  };

  return (
    <Select
      value={member.role}
      onValueChange={handleRoleChange}
      disabled={isCurrentUser}
    >
      <SelectTrigger className="h-8 w-[120px]">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="owner">Owner</SelectItem>
        <SelectItem value="admin">Admin</SelectItem>
        <SelectItem value="member">Member</SelectItem>
      </SelectContent>
    </Select>
  );
}

// ---------------------------------------------------------------------------
// Invite Member Dialog (owner-only)
// ---------------------------------------------------------------------------

/**
 * Dialog triggered by an "Invite Member" button. Contains an email input
 * and sends the invitation via the WorkOS-backed inviteMember action.
 * Invalidates the invitations query on success so the new pending invite
 * appears immediately in the table.
 */
function InviteMemberDialog({ orgId }: { orgId: Id<"organizations"> }) {
  const [open, setOpen] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const { mutateAsync: inviteMember, isPending } = useMutation({
    mutationFn: useConvexAction(api.organizations.mutations.inviteMember),
  });

  const form = useForm({
    defaultValues: {
      email: "",
    },
    onSubmit: async ({ value }) => {
      try {
        await inviteMember({ orgId, email: value.email });
        setSubmitError(null);
        toast.success(`Invitation sent to ${value.email}`);
        setOpen(false);
        form.reset();
        // Refetch pending invitations so the new one appears
        await queryClient.invalidateQueries({
          queryKey: invitationsQueryKey(orgId),
        });
      } catch (error: unknown) {
        const message = getInviteErrorMessage(error);
        setSubmitError(message);
      }
    },
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (nextOpen) {
          setSubmitError(null);
        }
      }}
    >
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="gap-2">
          <UserPlus className="h-4 w-4" />
          Invite Member
        </Button>
      </DialogTrigger>
      <DialogContent>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            form.handleSubmit();
          }}
        >
          <DialogHeader>
            <DialogTitle>Invite a Member</DialogTitle>
            <DialogDescription>
              Send an email invitation to join your organisation. They will
              receive a link to accept and create an account if needed.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <form.Field
              name="email"
              validators={{
                onSubmit: inviteEmailSchema,
              }}
              children={(field) => (
                <div className="flex flex-col gap-1">
                  <Input
                    type="email"
                    placeholder="colleague@example.com"
                    autoComplete="off"
                    required
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => {
                      if (submitError) {
                        setSubmitError(null);
                      }
                      field.handleChange(e.target.value);
                    }}
                    className={`bg-transparent ${
                      field.state.meta?.errors.length > 0
                        ? "border-destructive focus-visible:ring-destructive"
                        : ""
                    }`}
                  />
                  {field.state.meta?.errors.length > 0 && (
                    <p className="text-sm text-destructive dark:text-destructive-foreground">
                      {field.state.meta.errors
                        .map((e) => (typeof e === "string" ? e : e?.message))
                        .filter(Boolean)
                        .join(" ")}
                    </p>
                  )}
                </div>
              )}
            />
            {submitError && (
              <Alert variant="destructive" className="mt-3">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{submitError}</AlertDescription>
              </Alert>
            )}
          </div>

          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline">
                Cancel
              </Button>
            </DialogClose>
            <Button type="submit" disabled={isPending}>
              {isPending ? "Sending..." : "Send Invitation"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Shared Components
// ---------------------------------------------------------------------------

/**
 * Renders a role badge with variant based on role level.
 */
function RoleBadge({ role }: { role: OrgRole }) {
  const variant =
    role === "owner" ? "default" : role === "admin" ? "secondary" : "outline";

  return (
    <Badge variant={variant} className="capitalize">
      {role}
    </Badge>
  );
}

// ---------------------------------------------------------------------------
// Skeletons
// ---------------------------------------------------------------------------

function OrganizationSettingsSkeleton() {
  return (
    <div className="flex h-full w-full flex-col gap-8">
      {[1].map((i) => (
        <div key={i} className="flex flex-col gap-6">
          <div className="h-32 w-full animate-pulse rounded-lg bg-muted" />
          <div className="h-64 w-full animate-pulse rounded-lg bg-muted" />
        </div>
      ))}
    </div>
  );
}

function MembersTableSkeleton() {
  return (
    <div className="flex flex-col gap-3">
      {[1, 2, 3].map((i) => (
        <div key={i} className="h-10 w-full animate-pulse rounded bg-muted" />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Teams (MOR-722)
// ---------------------------------------------------------------------------

interface TeamItem {
  _id: Id<"teams">;
  _creationTime: number;
  name: string;
  members: Array<{
    userId: Id<"users">;
    email: string;
    firstName?: string;
    lastName?: string;
    avatarUrl?: string;
    /** MOR-748 — workflow role pre-assigned when the team joins a job. */
    defaultWorkflowRoleId?: Id<"workflowRoles">;
    /** MOR-748/MOR-867 — job role pre-selected when the team joins a job. */
    defaultJobRole?: JobRole;
  }>;
}

/**
 * Card listing the org's teams. Owners/admins can create, rename and delete
 * teams and manage their members; regular members get a read-only view.
 * Teams are used to bulk-add people to jobs and to drive the default job
 * access setting below.
 */
function TeamsCard({
  orgId,
  canManage,
  orgMembers,
}: {
  orgId: Id<"organizations">;
  canManage: boolean;
  orgMembers: OrgMember[];
}) {
  const { data: teams } = useQuery(
    convexQuery(api.organizations.teams.listTeams, { orgId }),
  );

  return (
    <div className={`flex w-full flex-col ${GROUP_CARD_CLASS}`}>
      <div className="flex items-center justify-between p-6 pb-0">
        <div className="flex flex-col gap-2">
          <h3 className="text-lg font-medium text-primary">Teams</h3>
          <p className="text-sm text-primary/60">
            Group members into teams so whole teams can be added to jobs in one
            step. A member can belong to multiple teams.
          </p>
        </div>
        {canManage && <CreateTeamDialog orgId={orgId} />}
      </div>

      <div className="p-6 pt-4">
        {teams === undefined ? (
          <div className="h-16 w-full animate-pulse rounded bg-muted" />
        ) : teams.length === 0 ? (
          <p className="text-sm text-primary/60">
            No teams yet.
            {canManage ? " Create one to get started." : ""}
          </p>
        ) : (
          <div className="flex flex-col gap-2">
            {teams.map((team: TeamItem) => (
              <div
                key={team._id}
                className="flex items-center justify-between gap-3 rounded-md border border-border/50 p-3"
              >
                <div className="flex min-w-0 items-center gap-3">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10">
                    <Users className="h-4 w-4 text-primary/60" />
                  </span>
                  <div className="flex min-w-0 flex-col">
                    <span className="truncate text-sm font-medium text-primary">
                      {team.name}
                    </span>
                    <span className="text-xs text-primary/60">
                      {team.members.length} member
                      {team.members.length !== 1 ? "s" : ""}
                    </span>
                  </div>
                  <div className="ml-1 flex -space-x-2">
                    {team.members.slice(0, 5).map((m) => (
                      <UserAvatar
                        key={m.userId}
                        avatarUrl={m.avatarUrl}
                        firstName={m.firstName}
                        lastName={m.lastName}
                        email={m.email}
                        className="h-6 w-6 border-2 border-background"
                      />
                    ))}
                    {team.members.length > 5 && (
                      <span className="flex h-6 w-6 items-center justify-center rounded-full border-2 border-background bg-muted text-[10px] text-primary/60">
                        +{team.members.length - 5}
                      </span>
                    )}
                  </div>
                </div>
                {canManage && (
                  <div className="flex shrink-0 items-center gap-1">
                    <ManageTeamDialog
                      orgId={orgId}
                      team={team}
                      orgMembers={orgMembers}
                    />
                    <DeleteTeamButton team={team} />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/** Dialog for creating a new team (owners/admins only). */
function CreateTeamDialog({ orgId }: { orgId: Id<"organizations"> }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");

  const { mutateAsync: createTeam, isPending } = useMutation({
    mutationFn: useConvexMutation(api.organizations.teams.createTeam),
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      toast.error("Enter a team name");
      return;
    }
    try {
      await createTeam({ orgId, name: trimmed });
      toast.success(`Team "${trimmed}" created`);
      setOpen(false);
      setName("");
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to create team";
      toast.error(message);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) setName("");
      }}
    >
      <DialogTrigger asChild>
        <Button size="sm" className="gap-1.5">
          <Users className="h-4 w-4" />
          Create Team
        </Button>
      </DialogTrigger>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Create a team</DialogTitle>
            <DialogDescription>
              Give the team a name, then add members from the manage view.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Input
              placeholder="e.g. Restructuring, Perth Office"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
            />
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline">
                Cancel
              </Button>
            </DialogClose>
            <Button type="submit" disabled={isPending || !name.trim()}>
              {isPending ? "Creating..." : "Create Team"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Dialog for renaming a team and managing its members (owners/admins). */
function ManageTeamDialog({
  orgId,
  team,
  orgMembers,
}: {
  orgId: Id<"organizations">;
  team: TeamItem;
  orgMembers: OrgMember[];
}) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(team.name);

  const { mutateAsync: renameTeam, isPending: isRenaming } = useMutation({
    mutationFn: useConvexMutation(api.organizations.teams.renameTeam),
  });
  const { mutateAsync: addTeamMember } = useMutation({
    mutationFn: useConvexMutation(api.organizations.teams.addTeamMember),
  });
  const { mutateAsync: removeTeamMember } = useMutation({
    mutationFn: useConvexMutation(api.organizations.teams.removeTeamMember),
  });
  const { mutateAsync: setMemberDefaults } = useMutation({
    mutationFn: useConvexMutation(
      api.organizations.teams.setTeamMemberDefaults,
    ),
  });

  // MOR-748 — the firm's workflow roles, offered as per-member defaults.
  const { data: workflowRoles } = useQuery({
    ...convexQuery(api.tasks.workflowRoles.listByOrg, { orgId }),
    enabled: open,
  });

  const teamMemberIds = new Set(team.members.map((m) => m.userId));
  const availableMembers = orgMembers.filter(
    (m) => !teamMemberIds.has(m.user._id),
  );

  const handleRename = async () => {
    const trimmed = name.trim();
    if (!trimmed || trimmed === team.name) return;
    try {
      await renameTeam({ teamId: team._id, name: trimmed });
      toast.success("Team renamed");
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to rename team";
      toast.error(message);
    }
  };

  const handleAdd = async (userId: Id<"users">) => {
    try {
      await addTeamMember({ teamId: team._id, userId });
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to add member";
      toast.error(message);
    }
  };

  const handleRemove = async (userId: Id<"users">) => {
    try {
      await removeTeamMember({ teamId: team._id, userId });
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to remove member";
      toast.error(message);
    }
  };

  // MOR-748/MOR-867 — per-member defaults applied when the team joins a job.
  const handleDefaultWorkflowRole = async (
    userId: Id<"users">,
    value: string,
  ) => {
    try {
      await setMemberDefaults({
        teamId: team._id,
        userId,
        defaultWorkflowRoleId:
          value === "__none__" ? null : (value as Id<"workflowRoles">),
      });
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to update default";
      toast.error(message);
    }
  };

  const handleDefaultJobRole = async (userId: Id<"users">, value: string) => {
    try {
      await setMemberDefaults({
        teamId: team._id,
        userId,
        defaultJobRole: value === "__none__" ? null : (value as JobRole),
      });
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to update default";
      toast.error(message);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (o) setName(team.name);
      }}
    >
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          Manage
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>Manage team</DialogTitle>
          <DialogDescription>
            Rename the team, choose who belongs to it, and set the roles they
            start with on a new job.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-5 py-4">
          {/* Rename */}
          <div className="flex items-end gap-2">
            <div className="flex flex-1 flex-col gap-1.5">
              <span className="text-xs font-medium text-primary/60">
                Team name
              </span>
              <Input value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={handleRename}
              disabled={isRenaming || !name.trim() || name.trim() === team.name}
            >
              Rename
            </Button>
          </div>

          {/* Members table + add-member picker */}
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-medium text-primary/60">
                Team members ({team.members.length})
              </span>
              <AddTeamMemberPicker
                availableMembers={availableMembers}
                onAdd={handleAdd}
              />
            </div>

            {team.members.length === 0 ? (
              <div className="rounded-md border border-dashed border-border/60 px-4 py-8 text-center text-sm text-primary/60">
                No members yet. Use Add member to build this team.
              </div>
            ) : (
              <div className="overflow-hidden rounded-md border border-border/60">
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-transparent">
                      <TableHead className="h-9 px-3 text-xs">Member</TableHead>
                      {/* MOR-748/MOR-867 — defaults applied when this team is
                          selected on a new job: the member's workflow role (who
                          role-based tasks go to) and their job-level role. */}
                      <TableHead className="h-9 w-[190px] px-3 text-xs">
                        <span className="inline-flex items-center gap-1">
                          Workflow role
                          <RoleHint title="Workflow role">
                            <p>
                              The slot this person fills in your firm's
                              workflows, such as Partner, Manager or Analyst.
                            </p>
                            <p>
                              Workflow templates assign tasks to a role instead
                              of a named person, so one workflow serves every
                              team. The role set here is pre-assigned to them
                              when this team is added to a job.
                            </p>
                          </RoleHint>
                        </span>
                      </TableHead>
                      <TableHead className="h-9 w-[150px] px-3 text-xs">
                        <span className="inline-flex items-center gap-1">
                          Job role
                          <RoleHint title="Job role">
                            <p>
                              What this person can do on the job itself, which
                              controls their access rather than their tasks.
                            </p>
                            <ul className="flex list-none flex-col gap-1">
                              <li>
                                <span className="font-medium text-primary">
                                  Owner
                                </span>
                                : full control, including the job team and
                                settings.
                              </li>
                              <li>
                                <span className="font-medium text-primary">
                                  Member
                                </span>
                                : can view and contribute.
                              </li>
                              <li>
                                <span className="font-medium text-primary">
                                  Viewer
                                </span>
                                : read-only access.
                              </li>
                            </ul>
                            <p>
                              Leave it on Default to let the job creator choose.
                            </p>
                          </RoleHint>
                        </span>
                      </TableHead>
                      <TableHead className="h-9 w-[52px] px-3" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {team.members.map((m) => (
                      <TableRow key={m.userId}>
                        <TableCell className="px-3 py-2">
                          <div className="flex min-w-0 items-center gap-2">
                            <UserAvatar
                              avatarUrl={m.avatarUrl}
                              firstName={m.firstName}
                              lastName={m.lastName}
                              email={m.email}
                              className="h-7 w-7 shrink-0"
                            />
                            <div className="flex min-w-0 flex-col">
                              <span className="truncate text-sm">
                                {getDisplayName(
                                  m.firstName,
                                  m.lastName,
                                  m.email,
                                )}
                              </span>
                              <span className="truncate text-xs text-primary/60">
                                {m.email}
                              </span>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="px-3 py-2">
                          <WorkflowRoleSelect
                            roles={workflowRoles ?? []}
                            value={m.defaultWorkflowRoleId ?? null}
                            onChange={(v) =>
                              handleDefaultWorkflowRole(
                                m.userId,
                                v ?? "__none__",
                              )
                            }
                          />
                        </TableCell>
                        <TableCell className="px-3 py-2">
                          <JobRoleSelect
                            value={m.defaultJobRole ?? null}
                            allowDefault
                            onChange={(role) =>
                              handleDefaultJobRole(m.userId, role ?? "__none__")
                            }
                          />
                        </TableCell>
                        <TableCell className="px-3 py-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            aria-label={`Remove ${getDisplayName(m.firstName, m.lastName, m.email)} from team`}
                            className="h-7 w-7 p-0 text-primary/60 hover:text-destructive"
                            onClick={() => handleRemove(m.userId)}
                          >
                            <X className="h-3.5 w-3.5" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="outline">
              Done
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Small "(i)" popover used beside the role column headers, so a manager can
 * see what a workflow role or a job role actually controls without leaving
 * the dialog.
 */
function RoleHint({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`What is a ${title.toLowerCase()}?`}
          className="inline-flex h-4 w-4 items-center justify-center rounded-full text-primary/50 transition-colors hover:text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <Info className="h-3.5 w-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80">
        <div className="flex flex-col gap-2">
          <span className="text-sm font-medium text-primary">{title}</span>
          <div className="flex flex-col gap-2 text-xs font-normal normal-case leading-relaxed text-primary/70">
            {children}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

/**
 * "Add member" button that opens a searchable list of org members not yet on
 * the team. Keeps the dialog readable when the org is large, and keeps the
 * managing view free of a long roster the manager did not ask to see.
 */
function AddTeamMemberPicker({
  availableMembers,
  onAdd,
}: {
  availableMembers: OrgMember[];
  onAdd: (userId: Id<"users">) => void | Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const noneLeft = availableMembers.length === 0;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-8 gap-1.5 text-xs"
          disabled={noneLeft}
          title={
            noneLeft
              ? "Everyone in the organisation is already on this team"
              : undefined
          }
        >
          <UserPlus className="h-3.5 w-3.5" />
          Add member
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[300px] p-0">
        <Command>
          <CommandInput
            placeholder="Search people..."
            className="h-9 text-xs"
          />
          <CommandList>
            <CommandEmpty className="py-4 text-center text-xs text-primary/60">
              No matching people.
            </CommandEmpty>
            <CommandGroup>
              {availableMembers.map((m) => {
                const displayName = getDisplayName(
                  m.user.firstName,
                  m.user.lastName,
                  m.user.email,
                );
                return (
                  <CommandItem
                    key={m.user._id}
                    // Both name and email are searchable.
                    value={`${displayName} ${m.user.email}`}
                    onSelect={() => {
                      void onAdd(m.user._id);
                      setOpen(false);
                    }}
                    className="gap-2"
                  >
                    <UserAvatar
                      avatarUrl={m.user.avatarUrl}
                      firstName={m.user.firstName}
                      lastName={m.user.lastName}
                      email={m.user.email}
                      className="h-6 w-6 shrink-0"
                    />
                    <div className="flex min-w-0 flex-col">
                      <span className="truncate text-xs">{displayName}</span>
                      <span className="truncate text-[11px] text-primary/60">
                        {m.user.email}
                      </span>
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

/** Delete-team button with confirmation (owners/admins). */
function DeleteTeamButton({ team }: { team: TeamItem }) {
  const { mutateAsync: deleteTeam, isPending } = useMutation({
    mutationFn: useConvexMutation(api.organizations.teams.deleteTeam),
  });

  const handleDelete = async () => {
    try {
      await deleteTeam({ teamId: team._id });
      toast.success(`Team "${team.name}" deleted`);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to delete team";
      toast.error(message);
    }
  };

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0 text-primary/60 hover:text-destructive"
        >
          <X className="h-4 w-4" />
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete "{team.name}"?</AlertDialogTitle>
          <AlertDialogDescription>
            The team and its member list will be removed. Members keep their
            access to any jobs the team was added to — this only deletes the
            grouping.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={handleDelete} disabled={isPending}>
            Delete Team
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// ---------------------------------------------------------------------------
// Default Job Access (MOR-722)
// ---------------------------------------------------------------------------

/**
 * Card controlling who gets pre-selected on newly created jobs: nobody
 * (default), all staff, or a specific team. Opt-in and admin-controlled so
 * org-wide access stays a deliberate decision; the job creator can always
 * adjust the selection before the job is created.
 */
function DefaultJobAccessCard({
  orgId,
  canManage,
}: {
  orgId: Id<"organizations">;
  canManage: boolean;
}) {
  const { data: accessDefault } = useQuery(
    convexQuery(api.organizations.teams.getJobAccessDefault, { orgId }),
  );
  const { data: teams } = useQuery(
    convexQuery(api.organizations.teams.listTeams, { orgId }),
  );

  const { mutateAsync: setDefault, isPending } = useMutation({
    mutationFn: useConvexMutation(api.organizations.teams.setJobAccessDefault),
  });

  // Encode the tri-state into a single select value ("team:<id>" for teams).
  const currentValue =
    accessDefault === undefined
      ? undefined
      : accessDefault.defaultAccess === "team" && accessDefault.defaultTeamId
        ? `team:${accessDefault.defaultTeamId}`
        : accessDefault.defaultAccess;

  const handleChange = async (value: string) => {
    try {
      if (value.startsWith("team:")) {
        await setDefault({
          orgId,
          defaultAccess: "team",
          defaultTeamId: value.slice("team:".length) as Id<"teams">,
        });
      } else {
        await setDefault({
          orgId,
          defaultAccess: value as "none" | "all_staff",
        });
      }
      toast.success("Default job access updated");
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to update setting";
      toast.error(message);
    }
  };

  const describeCurrent = () => {
    if (!accessDefault || accessDefault.defaultAccess === "none") {
      return "Only the people the job creator selects.";
    }
    if (accessDefault.defaultAccess === "all_staff") {
      return "All staff are pre-selected on new jobs.";
    }
    return `Members of "${accessDefault.defaultTeamName ?? "a team"}" are pre-selected on new jobs.`;
  };

  return (
    <div className={`flex w-full flex-col ${GROUP_CARD_CLASS}`}>
      <div className="flex flex-col gap-2 p-6 pb-3">
        <h3 className="text-lg font-medium text-primary">Default Job Access</h3>
        <p className="text-sm text-primary/60">
          Who is pre-selected when a new job is created. The job creator can
          always adjust the selection before creating the job, and every
          addition is recorded in the job's history.
        </p>
      </div>
      <div className="flex flex-col gap-3 px-6 pb-6">
        {canManage ? (
          <div className="max-w-sm">
            <Select
              value={currentValue}
              onValueChange={handleChange}
              disabled={isPending || accessDefault === undefined}
            >
              <SelectTrigger>
                <SelectValue placeholder="Loading..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">
                  No default — creator picks members
                </SelectItem>
                <SelectItem value="all_staff">All staff</SelectItem>
                {(teams ?? []).map((team: TeamItem) => (
                  <SelectItem key={team._id} value={`team:${team._id}`}>
                    Team: {team.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : (
          <p className="text-sm text-primary/80">{describeCurrent()}</p>
        )}
        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription className="text-xs">
            Adding all staff grants everyone access to every new job's data.
            Most firms should prefer a team default or no default, and add all
            staff per job when genuinely needed (e.g. leave coverage).
          </AlertDescription>
        </Alert>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Calendar & workflow tasks (MOR-836 / MOR-839)
// ---------------------------------------------------------------------------

/**
 * Card holding the firm-wide task/calendar defaults: the appointee
 * jurisdiction used for business-day due-date offsets (MOR-836) and
 * whether the platform's pre-populated statutory calendar tasks are
 * generated at all (MOR-839). Jobs can override both in Job Settings.
 */
function CalendarTasksCard({
  orgId,
  canManage,
}: {
  orgId: Id<"organizations">;
  canManage: boolean;
}) {
  const { data: settings } = useQuery(
    convexQuery(api.tasks.settings.getOrgTaskSettings, { orgId }),
  );
  const { mutateAsync: saveSettings, isPending } = useMutation({
    mutationFn: useConvexMutation(api.tasks.settings.setOrgTaskSettings),
  });

  const handleJurisdictionChange = async (value: string) => {
    try {
      await saveSettings({
        orgId,
        defaultAppointeeJurisdiction: value === "none" ? null : value,
      });
      toast.success("Default jurisdiction updated");
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to update setting";
      toast.error(message);
    }
  };

  const handleStatutoryChange = async (value: string) => {
    try {
      await saveSettings({
        orgId,
        disableStatutoryTasks: value === "hidden",
      });
      toast.success("Statutory calendar tasks setting updated");
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to update setting";
      toast.error(message);
    }
  };

  return (
    <div className={`flex w-full flex-col ${GROUP_CARD_CLASS}`}>
      <div className="flex flex-col gap-2 p-6 pb-3">
        <h3 className="text-lg font-medium text-primary">
          Calendar &amp; Workflow Tasks
        </h3>
        <p className="text-sm text-primary/60">
          Firm-wide defaults for job calendars and workflow due dates.
          Individual jobs can override both in Job Settings.
        </p>
      </div>
      <div className="flex flex-col gap-5 px-6 pb-6">
        <div className="flex flex-col gap-1.5">
          <p className="text-sm font-medium text-primary">
            Appointee jurisdiction
          </p>
          <p className="text-xs text-primary/60">
            Sets the public-holiday calendar used when workflow due dates are
            offset in business days.
          </p>
          {canManage ? (
            <div className="max-w-sm pt-1">
              <Select
                value={settings?.defaultAppointeeJurisdiction ?? "none"}
                onValueChange={handleJurisdictionChange}
                disabled={isPending || settings === undefined}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Loading..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Not set</SelectItem>
                  {AU_JURISDICTIONS.map((j) => (
                    <SelectItem key={j} value={j}>
                      {j}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : (
            <p className="text-sm text-primary/80">
              {settings?.defaultAppointeeJurisdiction ?? "Not set"}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <p className="text-sm font-medium text-primary">
            Pre-populated statutory calendar tasks
          </p>
          <p className="text-xs text-primary/60">
            The platform auto-generates statutory lodgement tasks (Form 505,
            DIRRI, annual returns, …) on every job's calendar. Hide them if your
            own workflow templates already create these tasks.
          </p>
          {canManage ? (
            <div className="max-w-sm pt-1">
              <Select
                value={
                  settings === undefined
                    ? undefined
                    : settings.disableStatutoryTasks
                      ? "hidden"
                      : "shown"
                }
                onValueChange={handleStatutoryChange}
                disabled={isPending || settings === undefined}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Loading..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="shown">Shown on all jobs</SelectItem>
                  <SelectItem value="hidden">
                    Hidden — our workflows cover them
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          ) : (
            <p className="text-sm text-primary/80">
              {settings?.disableStatutoryTasks
                ? "Hidden — covered by the firm's own workflows."
                : "Shown on all jobs."}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
